import type {
  LogPanelFilterEdit,
  SetLogPanelFiltersActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { SYNTHETIC_FILTER_TARGETS } from "@/shared/services/AiAgentThreadPubSubBus/models";
import { FILTER_CONDITIONS } from "@/shared/state/visualization/schema/v1/log-panel";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError } from "../ClientTools";

import { SetLogPanelFilterConfirmation } from "./confirmations";
import {
  MAX_FILTER_EDITS_PER_CALL,
  MAX_STAGES_PER_EDIT,
  mapEdit,
  type SetLogPanelFilterInput,
} from "./logPanelFilterEdit";
import { runPanelMutation, visualizerStatus } from "./workspaceActions";

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

function execute(
  input: SetLogPanelFilterInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!input.panel_id) {
    return toolError("panel_id is required");
  }
  if (!Array.isArray(input.edits) || input.edits.length === 0) {
    return toolError("edits is required and must be a non-empty array");
  }
  if (input.edits.length > MAX_FILTER_EDITS_PER_CALL) {
    return toolError(
      `At most ${MAX_FILTER_EDITS_PER_CALL} filter edits may be applied in one call.`,
    );
  }

  const edits: LogPanelFilterEdit[] = [];
  for (const [index, editInput] of input.edits.entries()) {
    const mapped = mapEdit(editInput, index);
    if ("error" in mapped) {
      return toolError(mapped.error);
    }
    edits.push(mapped.edit);
  }

  // Not `isOnVisualizerPage()`: inside a batch that closure answers the
  // pre-batch route, so it still reads true for a page a batch-mate is
  // replacing. Publishing into that workspace would report success against
  // panels that are about to be torn down.
  if (!deps?.publishWorkspaceAction || visualizerStatus(deps) !== "open") {
    return toolError(
      "The visualizer is not open, so there is no Log panel to filter. " +
        "Use client_visualize_open_panel to open the data first.",
    );
  }

  return runPanelMutation(
    deps.publishWorkspaceAction,
    "set_log_panel_filters",
    (
      onResult,
    ): {
      type: "set_log_panel_filters";
      payload: SetLogPanelFiltersActionPayload;
    } => ({
      type: "set_log_panel_filters",
      payload: {
        panelId: input.panel_id,
        edits,
        onResult,
      },
    }),
  );
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<SetLogPanelFilterInput>({
  name: "client_visualize_set_log_panel_filter",
  description: `Set, add to, or clear column filters on an existing Log panel — e.g. filter the message column to rows containing "error". The panel is referenced by its id from the visualizer state context. Target a column by the message_path_id of a signal on the panel, or use synthetic_target "all_columns" (searches every column; mutually exclusive with per-column filters, though a log_time filter may coexist with either) or "log_time" (the timestamp column; values are nanosecond-epoch strings). Give each target at most one edit per call. Every edit carries stages. mode "replace" swaps the column's filter (stages: [] clears it); "append" adds stages to it. Filtering defaults to hiding non-matching rows; this tool never changes that display mode. Opens the panel's filter bar automatically. If the result says applied is "pending_index_build", the panel is large and rows will not filter until its index is built — tell the user to click "Build Index" if they do not already see filtered rows. Only works while the user is on the visualizer page. On failure nothing is changed, and where the failure is per-target the error carries invalid_targets with a reason for each.`,
  input_schema: {
    type: "object",
    properties: {
      panel_id: {
        type: "string",
        description:
          "Id of the target Log panel. Panel ids are visible in the visualizer state context.",
      },
      edits: {
        type: "array",
        minItems: 1,
        maxItems: MAX_FILTER_EDITS_PER_CALL,
        description:
          "Filter edits, at most one per target column. Two edits that name the same column — or two message paths sharing one column — are rejected.",
        items: {
          type: "object",
          properties: {
            message_path_id: {
              type: "string",
              description:
                "Message path id of the column to filter (e.g. 'mp_abc123'). Must be on the panel. Provide this or synthetic_target, not both.",
            },
            synthetic_target: {
              type: "string",
              enum: [...SYNTHETIC_FILTER_TARGETS],
              description:
                "'all_columns' searches every column; 'log_time' filters the timestamp column (nanosecond-epoch string values).",
            },
            stages: {
              type: "array",
              maxItems: MAX_STAGES_PER_EDIT,
              description:
                "Filter stages; required. Pass [] with mode 'replace' to clear the column's filter.",
              items: {
                type: "object",
                properties: {
                  condition: {
                    type: "string",
                    enum: [...FILTER_CONDITIONS],
                    description:
                      "Numeric conditions (greater_than, less_than, between, ...) require numeric values and are only valid on numeric columns; is_empty/is_not_empty take no value.",
                  },
                  value: {
                    type: "string",
                    description:
                      "Filter value. Omit for is_empty/is_not_empty. Numeric conditions take decimal strings.",
                  },
                  value2: {
                    type: "string",
                    description: "Upper bound; required by 'between' only.",
                  },
                },
                required: ["condition"],
                additionalProperties: false,
              },
            },
            combinator: {
              type: "string",
              enum: ["or", "and"],
              description:
                "How the column's stages combine. Defaults to the column's current combinator, or 'or'.",
            },
            mode: {
              type: "string",
              enum: ["replace", "append"],
              description:
                "'replace' (default) swaps the column's filter wholesale; 'append' adds stages to it.",
            },
          },
          required: ["stages"],
          additionalProperties: false,
        },
      },
    },
    required: ["panel_id", "edits"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Filter Log Panel",
  displayDescription: "Sets or clears column filters on a Log panel.",
  ConfirmationContent: SetLogPanelFilterConfirmation,
});
